import { memo, useState } from 'react'
import { Link } from 'react-router'
import { Home } from 'lucide-react'
import { cn } from '@/lib/utils'

interface PropertyCellProps {
  propertyId: string
  name: string
  thumbnailUrl: string | null
  /** On a phone the column is the nickname alone. */
  narrow: boolean
  width: number
  height: number
}

/**
 * A row's sticky first cell: the property's main photo and its Uplisting
 * nickname, linking to the property. The nickname already says where the
 * property is, so there is no town line; a long one wraps to two lines, then
 * ends in an ellipsis, with the whole name on hover.
 */
export const PropertyCell = memo(function PropertyCell({
  propertyId,
  name,
  thumbnailUrl,
  narrow,
  width,
  height,
}: PropertyCellProps) {
  return (
    <Link
      to={`/properties/${encodeURIComponent(propertyId)}`}
      title={name}
      data-testid="portfolio-property"
      className={cn(
        'sticky left-0 z-10 flex shrink-0 items-center border-r border-border bg-surface transition-colors hover:bg-surface2 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-mint',
        narrow ? 'px-2.5' : 'gap-2.5 px-3'
      )}
      style={{ width, height }}
    >
      {/* Keyed by the URL: a new photo gets a fresh try, not the last one's failure. */}
      {!narrow && <PropertyThumbnail key={thumbnailUrl ?? ''} url={thumbnailUrl} />}
      <span
        className={cn(
          'line-clamp-2 min-w-0 font-semibold leading-[1.3] text-text [overflow-wrap:anywhere]',
          narrow ? 'text-xs' : 'text-[13px]'
        )}
      >
        {name}
      </span>
    </Link>
  )
})

const TILE_CLASSES = 'h-10 w-10 shrink-0 rounded-lg bg-surface2 ring-1 ring-border'

/** The photo, or a neutral tile when there is none or it does not load. */
function PropertyThumbnail({ url }: { url: string | null }) {
  const [failed, setFailed] = useState(false)
  if (!url || failed) {
    return (
      <span
        aria-hidden="true"
        data-testid="portfolio-property-tile"
        className={cn(TILE_CLASSES, 'flex items-center justify-center')}
      >
        <Home className="h-4 w-4 text-textFaint" />
      </span>
    )
  }
  return (
    <img
      src={url}
      alt=""
      width={40}
      height={40}
      loading="lazy"
      decoding="async"
      data-testid="portfolio-property-photo"
      onError={() => setFailed(true)}
      className={cn(TILE_CLASSES, 'object-cover')}
    />
  )
}
